import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { projectService } from '../services/projectService';
import { emailService } from '../services/emailService';
import { isValidEmail } from '../lib/format';
import { ErrorNote, Field, Modal } from './ui';

interface AddMemberModalProps {
  projectId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({ projectId, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const sendInvitation = async (memberId: string, address: string) => {
    const toastId = toast.loading(t('members.sendingInvite'));
    try {
      await emailService.sendMemberAddedNotification(memberId);
      toast.success(t('members.inviteSent', { email: address }), { id: toastId });
    } catch (err) {
      console.error('Failed to send member notification:', err);
      toast.error(
        (toastInstance) => (
          <span className="flex items-center gap-3">
            {t('members.inviteFailed')}
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => {
                toast.dismiss(toastInstance.id);
                sendInvitation(memberId, address);
              }}
            >
              {t('common.retry')}
            </button>
          </span>
        ),
        { id: toastId, duration: 10000 }
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!isValidEmail(email)) {
      setError(t('members.invalidEmail'));
      return;
    }
    setError('');
    setLoading(true);
    try {
      const memberId = await projectService.addMember(projectId, name, email, user.uid);
      toast.success(t('members.added', { name: name.trim() }));
      onSuccess();
      sendInvitation(memberId, email.trim());
    } catch (err: any) {
      console.error('Error adding member:', err);
      setError(err?.code === 'already-member' ? t('members.alreadyMember') : t('members.addFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={t('members.add')}
      description={t('members.addDescription')}
      onClose={onClose}
      busy={loading}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary" disabled={loading}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            form="add-member"
            className="btn btn-primary"
            disabled={loading || !name.trim() || !email.trim()}
          >
            {loading ? t('common.working') : t('members.addAndInvite')}
          </button>
        </>
      }
    >
      <form id="add-member" onSubmit={handleSubmit} className="space-y-4">
        <Field label={t('members.name')} htmlFor="member-name">
          <input id="member-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label={t('members.email')} htmlFor="member-email">
          <input
            id="member-email"
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            placeholder="nev@ceg.hu"
          />
        </Field>
        {error && <ErrorNote>{error}</ErrorNote>}
      </form>
    </Modal>
  );
};

export default AddMemberModal;
